import { CrudModel, CrudModelType } from "~/plugins/Cruxtify/CrudModel";
import { cache } from "./cache";
import { debounce } from "./debounce";

const querystring = require("querystring");
let context;
const commonHeaders = {};
export function setContext(nuxtContext) {
  context = nuxtContext;
  initAxiosConfig();
}

class AxiosError extends Error {
  public payload: any;
  public errors: Record<string, string[]> | undefined;
  public reference?: string;
  public debug?: any;
  public is_application_error?: boolean;
  public is_timeout_error?: boolean;
  // The originating axios response, preserved so callers can branch on the HTTP
  // status. Undefined only when the request never got one (a network error).
  public response?: any;
  public status?: number;

  constructor(message, payload, errors?) {
    super(message); // Call the base Error constructor with the message
    this.name = "AxiosError";
    this.payload = payload; // Attach additional data from the response
    this.errors = errors;
    this.is_application_error = payload?.is_application_error;
    this.is_timeout_error = payload?.is_timeout_error;
  }
}

function initAxiosConfig() {
  context.$axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Every AxiosError raised below carries the response through, so a status
      // the caller cares about (304, 404, 409) survives the translation instead
      // of collapsing into an opaque "Application Error".
      const withResponse = (axiosError: AxiosError) => {
        axiosError.response = error.response;
        axiosError.status = error.response?.status;

        return axiosError;
      };

      if (!error.response) {
        throw new AxiosError("Application Error", {
          is_application_error: true,
        });
      }

      if (error.response.status === 401 || error.response.status === 419) {
        // Only someone who HAD a session can have lost one. Logging out and
        // bouncing a visitor who was never logged in is meaningless, and it
        // destroys the page's own error handling: an `auth: false` page (the
        // one-click email responses) legitimately gets a 401 for a spent link
        // and has to render that, not be hard-reloaded onto /login. Absence of
        // `$auth` is NOT taken as "not logged in" — an app with no auth module
        // keeps the old behaviour rather than silently losing the redirect.
        const forcedLogout = context.$auth?.loggedIn !== false;

        // This branch tears the tab down, and used to do it silently: the only
        // thing that ever reached the error reporter was the AxiosError thrown
        // further down, which says nothing about whether the redirect fired or
        // what drove it. Record the decision and its inputs. The request's
        // query string is dropped — one-click links carry single-use tokens.
        context.$sentry?.addBreadcrumb?.({
          category: "cruxtify.api",
          level: "info",
          message: forcedLogout
            ? "auth rejected; forcing logout and redirect to /login"
            : "auth rejected with no session; leaving it to the caller",
          data: {
            status: error.response.status,
            loggedIn: context.$auth?.loggedIn ?? null,
            forcedLogout,
            requestPath: String(error.response.config?.url ?? "").split("?")[0],
            routePath: context.route?.path ?? null,
          },
        });

        if (forcedLogout) {
          context.$auth.logout();
          context.app.router.push({
            path: "/login",
          });

          // Temp fix for cache issues
          setTimeout(() => {
            location.reload();
          }, 500);
        }
      }

      if (error.response.status === 504) {
        throw withResponse(
          new AxiosError("Connection timed out.", {
            is_timeout_error: true,
          })
        );
      }

      if (error.response.data) {
        const errorMessage =
          error.response.data.message ?? error.response.data.error ?? "Error";
        const errorPayload = error.response.data.payload
          ? error.response.data.payload
          : {};
        const axiosError = new AxiosError(errorMessage, errorPayload, error.response.data.errors);
        axiosError.reference = error.response.data.error_reference;
        axiosError.debug = error.response.data.debug;
        throw withResponse(axiosError);
      }

      throw withResponse(
        new AxiosError("Application Error", {
          is_application_error: true,
        })
      );
    }
  );

  commonHeaders[
    "Access-Control-Allow-Origin"
  ] = `${context.$config.apiURL}:${context.$config.apiPort}`;
}

const _apiGet = (
  urlPath = "",
  query = {},
  urlPrefix = "api/",
  additionalParams = {}
) => {
  const args = {
    urlPath,
    query,
    urlPrefix,
    additionalParams,
  };

  // convert periods in query keys to brackets
  Object.keys(query).forEach((key) => {
    if (key.includes(".")) {
      query[key.replace(/\./g, "[") + "]"] = query[key];
      delete query[key];
    }
  });

  return debounce.debounceRequest(args, () => {
    const queryStr = querystring.stringify(query);
    return context.$axios
      .get(`${context.$config.apiURL}/${urlPrefix}${urlPath}/?${queryStr}`, {
        headers: commonHeaders,
        ...additionalParams,
      })
      .then((res) => res.data);
  });
};

const _apiPost = (
  urlPath = "",
  payload: object | FormData = {},
  urlPrefix = "api/",
  additionalAxiosParams: any = {},
  dataRequestOptions: Record<string, any> = {}
) => {
  let contentType = "application/json";
  if (payload instanceof FormData) {
    contentType = "multipart/form-data";
  }

  dataRequestOptions = new URLSearchParams(dataRequestOptions);

  return context.$axios
    .post(
      `${
        context.$config.apiURL
      }/${urlPrefix}${urlPath}/?${dataRequestOptions.toString()}`,
      payload,
      {
        headers: {
          ...commonHeaders,
          "Content-type": contentType,
          ...(additionalAxiosParams.headers
            ? additionalAxiosParams.headers
            : {}),
        },
        ...additionalAxiosParams,
      }
    )
    .then((res) => res.data);
};

const _apiPut = (
  urlPath = "",
  payload: Record<string, any> | FormData = {},
  urlPrefix = "api/",
  dataRequestOptions: Record<string, any> = {}
) => {
  let axiosMethod = "put";
  let contentType = "application/json";

  if (payload instanceof FormData) {
    payload.append("_method", "PUT");
    axiosMethod = "post";
    contentType = "multipart/form-data";
  }

  dataRequestOptions = new URLSearchParams(dataRequestOptions);

  return context.$axios[axiosMethod](
    `${
      context.$config.apiURL
    }/${urlPrefix}${urlPath}/?${dataRequestOptions.toString()}`,
    payload,
    {
      headers: {
        ...commonHeaders,
        "Content-type": contentType,
      },
    }
  ).then((res) => res.data);
};

const _apiDelete = (urlPath = "", urlPrefix = "api/") => {
  return context.$axios
    .delete(`${context.$config.apiURL}/${urlPrefix}${urlPath}/`, {
      headers: commonHeaders,
    })
    .then((res) => res.data);
};

const _buildUrl = (urlPath = "", query = {}, urlPrefix = "api/") => {
  const queryStr = querystring.stringify(query);
  return `${context.$config.apiURL}/${urlPrefix}${urlPath}?${queryStr}`;
};

export const login = (email: string, password: string) => {
  return context.$axios
    .get(`${context.$config.apiURL}/sanctum/csrf-cookie`, {
      headers: commonHeaders,
    })
    .then(() => {
      return context.$axios.post(
        `${context.$config.apiURL}/login`,
        {
          email,
          password,
        },
        { headers: commonHeaders }
      );
    });
};

const _buildModelRequestParams = (opts: ApiModelRequestOptions = {}) => {
  const params = {};

  if (typeof opts.onlyProperties !== "undefined") {
    params["_properties"] = opts.onlyProperties.join(",");
  }

  if (typeof opts.populate !== "undefined") {
    params["_populate"] = opts.populate.join(",");
  }

  return params;
};

type ApiModelRequestOptions = {
  onlyProperties?: string[];
  populate?: string[];
};

export type ApiCreateOptions = ApiModelRequestOptions;

export type ApiUpdateOptions = ApiModelRequestOptions;

export type ApiGetOptions = ApiModelRequestOptions;

export type ApiGetOneOptions = ApiGetOptions & ApiCachedOptions;

export type ApiCachedOptions = {
  noCache?: boolean;
};

export const api = {
  GET: _apiGet,
  POST: _apiPost,
  PUT: _apiPut,
  DELETE: _apiDelete,

  // crudmodel
  get: (modelType: CrudModelType, query = {}, opts: ApiGetOptions = {}) => {
    const path = modelType.api.path;

    return _apiGet(`${path}`, { ...query, ..._buildModelRequestParams(opts) });
  },
  getAll: (
    modelType: CrudModelType,
    query = {},
    opts: ApiGetOptions & ApiCachedOptions = {}
  ) => {
    if (cache.modelIsFullCached(modelType) && !opts.noCache)
      return cache.interceptGetAll(modelType);

    const path = modelType.api.path;

    const params = {
      _perPage: -1,
      ..._buildModelRequestParams(opts),
    };

    return _apiGet(`${path}`, params).then((res) => {
      return res.data.map((data) => new modelType(data));
    });
  },
  getOne: (
    modelType: CrudModelType,
    id: number,
    opts: ApiGetOneOptions = {}
  ) => {
    if (cache.modelIsFullCached(modelType, id) && !opts.noCache)
      return cache.interceptGetOne(modelType, id);

    const path = modelType.api.path;

    return _apiGet(`${path}/${id}`, _buildModelRequestParams(opts));
  },
  create: (model: CrudModel, opts: ApiCreateOptions = {}) => {
    const path = model.api.path;

    return _apiPost(
      `${path}`,
      model.toApiPayload(),
      "api/",
      {},
      _buildModelRequestParams(opts)
    ).then((res) => {
      // if (cache.modelIsCached(model)) {
      // TODO: refactor this once the hydration happens on the API instead of model
      const createdModel = new (model.getModelType())(res.data);
      cache.updateCachedModel(createdModel);
      // }
      return res;
    });
  },
  update: (model: CrudModel, opts: ApiUpdateOptions = {}) => {
    const path = model.api.path;
    if (model.isNew) return api.create(model, opts);

    const id = model.id;

    return _apiPut(
      `${path}/${id}`,
      model.toApiPayload(),
      "api/",
      _buildModelRequestParams(opts)
    ).then((res) => {
      // if (cache.modelIsCached(model)) {
      // TODO: refactor this once the hydration happens on the API instead of model
      const updatedModel = new (model.getModelType())(res.data);
      cache.updateCachedModel(updatedModel);
      // }
      return res;
    });
  },
  delete: (model: CrudModel) => {
    const path = model.api.path;
    const id = model.id;

    return _apiDelete(`${path}/${id}`);
  },

  export: (modelType: CrudModelType, query = {}) => {
    const path = modelType.api.path;

    return _apiGet(`${path}/export`, query, "api/", {
      responseType: "blob",
      headers: {
        Accept: "application/vnd.ms-excel",
      },
    });
  },

  // auth/user
  me: () => _apiGet("user"),
  registerUser: (userData: Record<string, any>) =>
    _apiPost("register", userData, ""),
  passwordForgot: (email: string) => _apiPost("forgot-password", { email }, ""),
  passwordReset: (email: string, password: string, token: string) =>
    _apiPost(
      "reset-password",
      {
        token,
        email,
        password,
        password_confirmation: password,
      },
      ""
    ),

  // stripe
  getStripeIntent: () => _apiGet("stripeIntent"),

  // misc
  buildUrl: _buildUrl,

  // cache
  cache,
};
